import { AlertCircle, Copy, Loader2 } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useDuplicateCongress } from "@/features/congress-details/api/duplicate-congress";
import { useCongressMeta } from "@/features/congress-details/api/get-congress";
import { CongressDetailsForm } from "@/features/congress-details/components/congress-details-form";
import { DeleteCongressModal } from "@/features/congress-details/components/delete-congress-modal";
import { MigrationModal } from "@/features/congress-details/components/migration-modal";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { useToast } from "@/hooks/use-toast";
import { pageTitle } from "@/utils/page-title";
import { useTranslation } from "react-i18next";

function DashboardPage() {
  const { t } = useTranslation();
  const {
    congress: { id: congressId },
    setCongress,
  } = useCongress();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { toast } = useToast();
  const {
    data: congress,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useCongressMeta(congressId);
  const { mutateAsync: duplicateCongress, isPending: isDuplicating } =
    useDuplicateCongress(congressId);

  const handleDuplicate = async () => {
    try {
      const newCongress = await duplicateCongress();
      setCongress(newCongress);
      toast({
        title: t("dashboard.duplicateSuccessTitle"),
        description: t("dashboard.duplicateSuccessDescription", {
          name: newCongress.name,
        }),
      });
    } catch {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("dashboard.duplicateErrorDescription"),
      });
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:flex-col">
      <title>{pageTitle(t("meta.dashboard"))}</title>

      <Card className="col-span-12">
        <CardHeader className="flex flex-row items-center justify-between font-semibold">
          {t("dashboard.congress")}
          <MigrationModal />
        </CardHeader>
        <CardContent className="relative">
          {isLoading && (
            <div className="flex h-60 place-content-center">
              <Loader2 className="size-6 animate-spin text-primary" />
            </div>
          )}
          {isError && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{t("common.errorTitle")}</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          {isSuccess && <CongressDetailsForm initialData={congress} />}
        </CardContent>
      </Card>

      {isSuccess && congress && (
        <Card className="col-span-12">
          <CardHeader>
            <h3 className="text-lg font-semibold">{t("dashboard.duplicateTitle")}</h3>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t("dashboard.duplicateDescription")}
            </p>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={handleDuplicate}
              disabled={isDuplicating}
            >
              {isDuplicating ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Copy className="mr-2 size-4" />
              )}
              {t("dashboard.duplicateButton")}
            </Button>
          </CardContent>
        </Card>
      )}

      {isSuccess && congress && (
        <Card className="col-span-12 border-destructive/50">
          <CardHeader>
            <h3 className="text-lg font-semibold text-destructive">
              {t("dashboard.dangerTitle")}
            </h3>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t("dashboard.dangerDescription")}
            </p>
            <Button
              variant="destructive"
              className="mt-4"
              onClick={() => setDeleteModalOpen(true)}
            >
              {t("dashboard.deleteButton")}
            </Button>
          </CardContent>
        </Card>
      )}

      {congress && (
        <DeleteCongressModal
          congress={congress}
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
        />
      )}
    </div>
  );
}

export { DashboardPage };
